/*
 * Woerdman — Cadeau bij aankoop (gift with purchase)
 * -------------------------------------------------------------
 * Voegt automatisch een gratis cadeauproduct toe zodra er een product uit de
 * ingestelde trigger-collectie (pannen) in de winkelwagen ligt, en haalt het
 * cadeau er weer uit zodra dat product verdwijnt.
 *
 * PER PAN: het aantal cadeaus volgt het aantal pan-eenheden in het mandje
 * (2 pannen -> 2 cadeaus, enz.). Op- en afschalen gebeurt automatisch.
 *
 * De prijs (gratis) regelt een aparte automatische Shopify-korting
 * ("Koop X, krijg Y"). Deze code regelt alleen toevoegen/verwijderen.
 *
 * Instellingen komen uit de thema-instellingen (settings.gift_*), doorgegeven
 * via data-attributen op het <gift-with-purchase>-element.
 */
if (!customElements.get('gift-with-purchase')) {
  customElements.define(
    'gift-with-purchase',
    class GiftWithPurchase extends HTMLElement {
      connectedCallback() {
        this.enabled = this.dataset.enabled === 'true';
        this.giftVariantId = parseInt(this.dataset.giftVariantId, 10);
        this.giftProductId = parseInt(this.dataset.giftProductId, 10);
        this.collectionHandle = this.dataset.collectionHandle;
        this.message = this.dataset.message || 'Cadeau';
        // Drempel-modus (optioneel): cadeau zodra de som van trigger-items >= minAmount (in centen).
        // Zonder data-min-amount = per-stuk-modus (1 cadeau per trigger-eenheid).
        this.minAmount = parseInt(this.dataset.minAmount, 10) || 0;

        this.busy = false; // we muteren zelf -> negeer onze eigen cart-updates (geen loops)
        this.addBlocked = false; // cadeau kon niet toegevoegd worden (bv. uitverkocht)
        this.eligibleIds = null;

        if (!this.enabled || !this.giftVariantId || !this.giftProductId || !this.collectionHandle) return;
        if (!window.FoxTheme || !FoxTheme.pubsub) return;

        // Debounce: wacht tot het mandje tot rust komt na een wijziging, zodat we
        // niet racen met het toevoegen/wijzigen door het thema zelf.
        this.debouncedReconcile = FoxTheme.utils.debounce(this.reconcileFromServer.bind(this), 350);
        this.unsub = FoxTheme.pubsub.subscribe(
          FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate,
          this.onCartUpdate.bind(this)
        );

        // Ook 1x bij laden checken (terugkerende bezoeker met al een pan in het mandje).
        if (!(window.Shopify && Shopify.designMode)) {
          this.reconcileFromServer();
        }
      }

      disconnectedCallback() {
        if (this.unsub) this.unsub();
      }

      /* Product-id's van de trigger-collectie ophalen (gepagineerd) en cachen. */
      async loadEligibleIds() {
        if (this.eligibleIds) return this.eligibleIds;
        const cacheKey = 'wgift:' + this.collectionHandle;
        try {
          const cached = sessionStorage.getItem(cacheKey);
          if (cached) {
            this.eligibleIds = new Set(JSON.parse(cached));
            return this.eligibleIds;
          }
        } catch (e) {}

        const ids = new Set();
        const limit = 250;
        let page = 1;
        while (page <= 10) {
          let res;
          try {
            res = await fetch(`/collections/${this.collectionHandle}/products.json?limit=${limit}&page=${page}`);
          } catch (e) {
            break;
          }
          if (!res.ok) break;
          const data = await res.json();
          const products = (data && data.products) || [];
          products.forEach((p) => ids.add(p.id));
          if (products.length < limit) break;
          page++;
        }

        this.eligibleIds = ids;
        try {
          sessionStorage.setItem(cacheKey, JSON.stringify([...ids]));
        } catch (e) {}
        return ids;
      }

      async getCart() {
        return (await fetch(`${FoxTheme.routes.cart_url}.js`)).json();
      }

      /*
       * De cadeauregels van DEZE actie: alleen het eigen cadeauproduct én
       * gemarkeerd met _gift. Zo blijven (a) andere cadeau-acties en (b) een
       * door de klant zélf gekocht exemplaar van hetzelfde product ongemoeid.
       */
      giftLinesOf(cart) {
        return cart.items.filter(
          (i) => i.product_id === this.giftProductId && i.properties && i.properties._gift
        );
      }

      /* Totaal aantal pan-eenheden (som van hoeveelheden) in het mandje. */
      panUnits(cart) {
        if (!this.eligibleIds) return 0;
        return cart.items.reduce((s, i) => {
          if (i.product_id !== this.giftProductId && this.eligibleIds.has(i.product_id)) return s + i.quantity;
          return s;
        }, 0);
      }

      async reconcileFromServer() {
        if (this.busy) return;
        try {
          await this.reconcile(await this.getCart());
        } catch (e) {}
      }

      onCartUpdate() {
        if (window.Shopify && Shopify.designMode) return;
        this.debouncedReconcile();
      }

      /* Zorgt dat het aantal cadeaus klopt met 'target' (0 of 1). */
      async reconcile(cart) {
        if (this.busy || !cart || !cart.items) return;
        await this.loadEligibleIds();

        const giftLines = this.giftLinesOf(cart);
        const giftUnits = giftLines.reduce((s, l) => s + l.quantity, 0);

        // Doel-aantal cadeaus bepalen op basis van de modus:
        let target;
        if (this.minAmount > 0) {
          // Drempel-modus: 1 cadeau zodra de som van de trigger-items (bv. messen) >= minAmount.
          const sum = cart.items.reduce((s, i) => {
            if (i.product_id !== this.giftProductId && this.eligibleIds.has(i.product_id)) return s + i.original_line_price;
            return s;
          }, 0);
          target = sum >= this.minAmount ? 1 : 0;
        } else {
          // Per-stuk-modus: 1 cadeau per trigger-eenheid (bv. per pan).
          target = this.panUnits(cart);
        }

        // Klopt al (en niet opgesplitst): niks doen.
        if (giftUnits === target && giftLines.length <= 1) {
          if (target > 0) this.addBlocked = false;
          return;
        }

        // Te veel cadeaus (of geen pan meer): overschot verwijderen, regel voor regel.
        if (giftUnits > target || (giftUnits === target && giftLines.length > 1)) {
          let toRemove = giftUnits - target;
          this.busy = true;
          try {
            for (const line of giftLines) {
              if (toRemove <= 0 && giftLines.length <= 1) break;
              const newQty = toRemove > 0 ? Math.max(0, line.quantity - toRemove) : line.quantity;
              if (newQty !== line.quantity) {
                toRemove -= line.quantity - newQty;
                await this.change(line.key, newQty);
              }
            }
          } catch (e) {} finally {
            this.busy = false;
          }
          await this.refreshCart();
          return;
        }

        // Te weinig cadeaus: aanvullen tot target.
        if (giftUnits < target && !this.addBlocked) {
          this.busy = true;
          let ok = false;
          try {
            ok = await this.add(target - giftUnits);
          } catch (e) {
            ok = false;
          } finally {
            this.busy = false;
          }
          if (ok) {
            this.addBlocked = false;
            await this.refreshCart();
          } else {
            this.addBlocked = true; // waarschijnlijk uitverkocht; pan-bestelling gaat gewoon door
          }
        }
      }

      async add(qty) {
        const res = await fetch(`${FoxTheme.routes.cart_add_url}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            items: [{ id: this.giftVariantId, quantity: qty, properties: { _gift: this.message } }],
          }),
        });
        const json = await res.json();
        return res.ok && !json.status; // json.status gezet bij fout
      }

      async change(key, qty) {
        await fetch(`${FoxTheme.routes.cart_url}/change.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: key, quantity: qty }),
        });
      }

      /*
       * Ververst precies zoals het thema zelf doet: verzamelt de betrokken
       * secties (winkelwagen-lade EN /winkelwagen-pagina) via 'cart:grouped-sections',
       * haalt die op en publiceert cartUpdate MET sections. Zo herrenderen beide
       * netjes en kloppen de regel-verwijzingen (geen "line is ongeldig").
       */
      async refreshCart() {
        try {
          const sectionIds = [];
          document.documentElement.dispatchEvent(
            new CustomEvent('cart:grouped-sections', { bubbles: true, detail: { sections: sectionIds } })
          );
          const ids = [...new Set(sectionIds)].filter(Boolean);
          const cart = await this.getCart();
          if (ids.length) {
            const base = FoxTheme.routes.root_url && FoxTheme.routes.root_url.length ? FoxTheme.routes.root_url : '/';
            const sep = base.indexOf('?') > -1 ? '&' : '?';
            const res = await fetch(`${base}${sep}sections=${ids.join(',')}`);
            if (res.ok) cart.sections = await res.json();
          }
          FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, { cart });
        } catch (e) {}
      }
    }
  );
}
